// @ts-check

/**
 * @file Initializes translation tables and locale selection for the frontend.
 */

import gettextConfig from "gettext-universal/build/src/config.js"
import translate from "gettext-universal/build/src/translate.js"
import HayaSelectConfig from "haya-select/build/config"

import {getStoredLocale, saveStoredLocale} from "../shared/locale-option"
import da from "./locales/da.json"
import de from "./locales/de.json"
import en from "./locales/en.json"

const SUPPORTED_LOCALES = ["en", "da", "de"]
const FALLBACK_LOCALE = "en"

const shared = {
  registered: false
}

/**
 * Normalizes a browser, stored, or profile locale into a supported locale code.
 * @param {string | null | undefined} candidate Locale value to normalize.
 * @returns {string | null} Supported locale code when one is available.
 */
function normalizeLocale(candidate) {
  if (typeof candidate !== "string" || candidate.length === 0) return null

  const code = candidate.slice(0, 2).toLowerCase()

  return SUPPORTED_LOCALES.includes(code) ? code : null
}

/**
 * Lists locale candidates reported by the browser.
 * @returns {string[]} Browser locale candidates in priority order.
 */
function deviceLocaleCandidates() {
  /** @type {string[]} */
  const candidates = []

  if (typeof navigator === "undefined") return candidates
  if (Array.isArray(navigator.languages)) candidates.push(...navigator.languages)
  if (typeof navigator.language === "string") candidates.push(navigator.language)

  return candidates
}

/**
 * Registers translation tables and the runtime fallback chain.
 * Idempotent on every subsequent call. Called once from the root layout module
 * so route screens render after the tables are registered. Does NOT pick a
 * locale — the authoritative locale is chosen exactly once at app boot via
 * `resolveStartupLocale()` once the user is known.
 * @returns {void} Nothing.
 */
export default function initializeTranslations() {
  if (shared.registered) return

  const availableLocales = {da, de, en}
  const localeData = gettextConfig.getLocales()

  Object.entries(availableLocales).forEach(([localeKey, translations]) => {
    localeData[localeKey] ||= translations
  })

  // English-only fallback. The previous chain `["en", "da", "de"]`
  // routed an English-locale lookup that missed `en.json` (which is
  // less complete than `da.json` / `de.json`) on through to Danish,
  // so switching from `da` to `en` left Danish strings on screen.
  // With just `["en"]`, en-locale misses fall through to the msgId
  // (the English source) instead, and da/de users still get English
  // for keys their own locale is missing.
  gettextConfig.setFallbacks([FALLBACK_LOCALE])
  HayaSelectConfig.current().setUseTranslate(() => ({t: translate}))
  shared.registered = true
}

/**
 * Picks the boot-time locale by priority — signed-in user locale →
 * previously-saved Option locale → device languages (in order) →
 * English — applies it via `gettextConfig.setLocale`, and persists
 * the chosen value into the Option store so the next cold boot
 * starts from a remembered value instead of re-deriving from
 * `navigator.languages` (which can shift between reloads). Call
 * exactly once per app boot from the current-user provider.
 * @param {string | null | undefined} userLocale Locale from the signed-in profile.
 * @returns {Promise<string>} Locale applied for the current boot.
 */
export async function resolveStartupLocale(userLocale) {
  let resolved = normalizeLocale(userLocale)

  if (!resolved) {
    const stored = await getStoredLocale()

    resolved = normalizeLocale(stored)
  }

  if (!resolved) {
    for (const candidate of deviceLocaleCandidates()) {
      const match = normalizeLocale(candidate)

      if (match) {
        resolved = match
        break
      }
    }
  }

  resolved ||= FALLBACK_LOCALE

  if (gettextConfig.getLocale() !== resolved) gettextConfig.setLocale(resolved)
  await saveStoredLocale(resolved)

  return resolved
}

/**
 * Applies a known locale and persists it to the Option store.
 * No-op for unsupported values.
 * @param {string | null | undefined} locale Locale selected by the user or profile.
 * @returns {Promise<void>} Resolves once the locale is stored.
 */
export async function applyLocale(locale) {
  const normalized = normalizeLocale(locale)

  if (!normalized) return

  if (gettextConfig.getLocale() !== normalized) gettextConfig.setLocale(normalized)
  await saveStoredLocale(normalized)
}
